if (!customElements.get('filazoo-bulk-sale')) {
  customElements.define(
    'filazoo-bulk-sale',
    class FilazooBulkSale extends HTMLElement {
      constructor() {
        super();
        this.cards = Array.from(this.querySelectorAll('[data-bulk-card]'));
        this.productFilter = 'all';
        this.spoolFilter = 'all';
        this.previousQuantity = 0;
        this.celebrationTimer = null;
        this.tiers = [
          { quantity: Number(this.dataset.tierOneQuantity), discount: Number(this.dataset.tierOneDiscount) },
          { quantity: Number(this.dataset.tierTwoQuantity), discount: Number(this.dataset.tierTwoDiscount) },
          { quantity: Number(this.dataset.tierThreeQuantity), discount: Number(this.dataset.tierThreeDiscount) },
        ].sort((a, b) => a.quantity - b.quantity);
      }

      connectedCallback() {
        if (!this.cards.length || this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.initializeSpoolFilters();
        this.bindDropdowns();
        this.bindFilters();
        this.bindQuantities();
        this.querySelector('[data-bulk-add]')?.addEventListener('click', () => this.submit(false));
        this.querySelector('[data-bulk-buy]')?.addEventListener('click', () => this.submit(true));
        this.querySelector('[data-bulk-toggle]')?.addEventListener('click', (event) => {
          const collapsed = this.classList.toggle('is-bar-collapsed');
          event.currentTarget.setAttribute('aria-expanded', String(!collapsed));
          event.currentTarget.setAttribute('aria-label', `${collapsed ? 'Show' : 'Hide'} bulk order details`);
          const label = event.currentTarget.querySelector('[data-bulk-toggle-label]');
          if (label) label.textContent = collapsed ? 'Show' : 'Hide';
        });
        this.update();
      }

      initializeSpoolFilters() {
        const values = [...new Set(this.cards.map((card) => card.dataset.spool.trim()).filter(Boolean))];
        const container = this.querySelector('[data-bulk-spool-filters]');

        // No spool data on any card means the only option would be "All", so
        // hide the control rather than show a dropdown that cannot filter.
        if (!values.length) {
          container?.closest('[data-bulk-dropdown]')?.setAttribute('hidden', '');
          return;
        }

        values.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'filazoo-bulk-sale__filter';
          button.setAttribute('role', 'option');
          button.dataset.spoolFilter = value;
          button.setAttribute('aria-selected', 'false');
          button.textContent = value;
          container.appendChild(button);
        });
      }

      bindDropdowns() {
        this.dropdowns = Array.from(this.querySelectorAll('[data-bulk-dropdown]'));

        this.dropdowns.forEach((dropdown) => {
          const trigger = dropdown.querySelector('[data-bulk-dropdown-trigger]');
          trigger?.addEventListener('click', (event) => {
            event.stopPropagation();
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';
            this.closeDropdowns();
            if (!isOpen) this.openDropdown(dropdown);
          });
        });

        // Clicking away or pressing Escape closes an open menu.
        this.onDocumentClick = (event) => {
          if (!event.target.closest('[data-bulk-dropdown]')) this.closeDropdowns();
        };
        this.onDocumentKeydown = (event) => {
          if (event.key !== 'Escape') return;
          const open = this.dropdowns.find(
            (d) => d.querySelector('[data-bulk-dropdown-trigger]')?.getAttribute('aria-expanded') === 'true'
          );
          if (!open) return;
          this.closeDropdowns();
          open.querySelector('[data-bulk-dropdown-trigger]')?.focus();
        };
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      openDropdown(dropdown) {
        const trigger = dropdown.querySelector('[data-bulk-dropdown-trigger]');
        const menu = dropdown.querySelector('[data-bulk-dropdown-menu]');
        if (!trigger || !menu) return;
        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
        dropdown.classList.add('is-open');
        menu.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
      }

      closeDropdowns() {
        this.dropdowns?.forEach((dropdown) => {
          dropdown.querySelector('[data-bulk-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
          const menu = dropdown.querySelector('[data-bulk-dropdown-menu]');
          if (menu) menu.hidden = true;
          dropdown.classList.remove('is-open');
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      bindFilters() {
        this.addEventListener('click', (event) => {
          const productButton = event.target.closest('[data-product-filter]');
          const spoolButton = event.target.closest('[data-spool-filter]');
          if (!productButton && !spoolButton) return;

          const selector = productButton ? '[data-product-filter]' : '[data-spool-filter]';
          const attribute = productButton ? 'productFilter' : 'spoolFilter';
          const value = productButton ? productButton.dataset.productFilter : spoolButton.dataset.spoolFilter;
          this[attribute] = value;

          const container = (productButton || spoolButton).parentElement;
          const chosen = productButton || spoolButton;
          container.querySelectorAll(selector).forEach((button) => {
            const active = button === chosen;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-selected', String(active));
          });

          // Mirror the choice onto the closed trigger, then dismiss the menu.
          const dropdown = chosen.closest('[data-bulk-dropdown]');
          const valueLabel = dropdown?.querySelector('[data-bulk-dropdown-value]');
          if (valueLabel) valueLabel.textContent = chosen.textContent.trim();
          this.closeDropdowns();
          dropdown?.querySelector('[data-bulk-dropdown-trigger]')?.focus();

          this.filterCards();
        });
      }

      bindQuantities() {
        this.cards.forEach((card) => {
          const input = card.querySelector('[data-bulk-input]');
          if (!input) return;

          card.querySelector('[data-bulk-decrement]')?.addEventListener('click', () => {
            input.value = String(Math.max(0, Number(input.value || 0) - 1));
            this.update();
          });
          card.querySelector('[data-bulk-increment]')?.addEventListener('click', () => {
            input.value = String(Math.max(0, Number(input.value || 0) + 1));
            this.update();
          });
          input.addEventListener('change', () => {
            input.value = String(Math.max(0, Math.floor(Number(input.value || 0))));
            this.update();
          });
        });
      }

      filterCards() {
        let visible = 0;
        this.cards.forEach((card) => {
          const productMatches = this.productFilter === 'all' || card.dataset.productHandle === this.productFilter;
          const spoolMatches = this.spoolFilter === 'all' || card.dataset.spool === this.spoolFilter;
          const show = productMatches && spoolMatches;
          card.hidden = !show;
          if (show) visible += 1;
        });
        const results = this.querySelector('[data-bulk-results]');
        if (results) results.textContent = `${visible} variant${visible === 1 ? '' : 's'}`;
      }

      getSelection() {
        return this.cards
          .map((card) => ({
            id: Number(card.dataset.variantId),
            quantity: Math.max(0, Number(card.querySelector('[data-bulk-input]')?.value || 0)),
            price: Number(card.dataset.price || 0),
          }))
          .filter((item) => item.quantity > 0);
      }

      getActiveTier(quantity) {
        return [...this.tiers].reverse().find((tier) => quantity >= tier.quantity) || null;
      }

      update() {
        const selection = this.getSelection();
        const quantity = selection.reduce((total, item) => total + item.quantity, 0);
        const subtotal = selection.reduce((total, item) => total + item.price * item.quantity, 0);
        const activeTier = this.getActiveTier(quantity);
        const total = activeTier ? Math.round(subtotal * (1 - activeTier.discount / 100)) : subtotal;
        const nextTier = this.tiers.find((tier) => quantity < tier.quantity);
        const bar = this.querySelector('[data-bulk-bar]');
        const isComplete = !nextTier && quantity > 0;
        bar?.classList.toggle('is-complete', isComplete);
        const badge = this.querySelector('[data-bulk-badge]');
        if (badge) badge.textContent = isComplete ? '✓ Best offer unlocked' : '🔥 Bulk savings';

        this.querySelector('[data-bulk-count]').textContent = quantity;
        const totalParts = this.formatMoneyParts(total);
        this.querySelector('[data-bulk-total-major]').textContent = totalParts.major;
        this.querySelector('[data-bulk-total-decimal]').textContent = totalParts.decimal;
        this.querySelectorAll('[data-bulk-add], [data-bulk-buy]').forEach((button) => (button.disabled = quantity === 0));

        const message = this.querySelector('[data-bulk-message]');
        if (nextTier) {
          const remaining = nextTier.quantity - quantity;
          const unlocked = activeTier ? `${activeTier.discount}% unlocked · ` : '';
          message.textContent = `${unlocked}Add ${remaining} more ${remaining === 1 ? 'item' : 'items'} to unlock ${nextTier.discount}% OFF.`;
        } else {
          message.textContent = `Best offer unlocked! You’re saving ${activeTier.discount}%.`;
        }

        const fill = this.querySelector('[data-bulk-progress-fill]');
        if (fill) fill.style.width = `${this.getProgressPercent(quantity)}%`;
        const progress = this.querySelector('[data-bulk-progress]');
        progress?.setAttribute('aria-valuenow', String(Math.min(quantity, this.tiers[this.tiers.length - 1].quantity)));
        this.querySelectorAll('[data-bulk-milestone]').forEach((milestone) => {
          milestone.classList.toggle('is-unlocked', quantity >= Number(milestone.dataset.bulkMilestone));
        });
        const newlyUnlockedTier = [...this.tiers]
          .reverse()
          .find((tier) => this.previousQuantity < tier.quantity && quantity >= tier.quantity);
        if (newlyUnlockedTier) this.celebrateTier(newlyUnlockedTier.quantity);
        this.previousQuantity = quantity;
        this.hideError();
      }

      celebrateTier(quantity) {
        const milestone = this.querySelector(`[data-bulk-milestone="${quantity}"]`);
        const bar = this.querySelector('[data-bulk-bar]');
        if (!milestone || !bar) return;
        const isBestTier = quantity === this.tiers[this.tiers.length - 1].quantity;

        window.clearTimeout(this.celebrationTimer);
        this.querySelectorAll('[data-bulk-milestone]').forEach((item) => item.classList.remove('is-tier-celebrating'));
        bar.classList.remove('is-unlock-celebrating');
        void bar.offsetWidth;
        milestone.classList.add('is-tier-celebrating');
        if (isBestTier) bar.classList.add('is-unlock-celebrating');

        this.celebrationTimer = window.setTimeout(() => {
          milestone.classList.remove('is-tier-celebrating');
          bar.classList.remove('is-unlock-celebrating');
        }, 1100);
      }

      getProgressPercent(quantity) {
        const positions = [10, 50, 90];
        const safeQuantity = Math.max(0, quantity);
        if (safeQuantity === 0) return 0;

        for (let index = 0; index < this.tiers.length; index += 1) {
          const upperQuantity = this.tiers[index].quantity;
          const lowerQuantity = index === 0 ? 0 : this.tiers[index - 1].quantity;
          const upperPosition = positions[index];
          const lowerPosition = index === 0 ? 0 : positions[index - 1];

          if (safeQuantity <= upperQuantity) {
            const segmentProgress = (safeQuantity - lowerQuantity) / Math.max(1, upperQuantity - lowerQuantity);
            return lowerPosition + segmentProgress * (upperPosition - lowerPosition);
          }
        }

        return positions[positions.length - 1];
      }

      formatMoneyParts(cents) {
        const parts = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).formatToParts(cents / 100);
        const decimalIndex = parts.findIndex((part) => part.type === 'decimal');
        if (decimalIndex === -1) return { major: parts.map((part) => part.value).join(''), decimal: '' };
        return {
          major: parts.slice(0, decimalIndex).map((part) => part.value).join(''),
          decimal: parts.slice(decimalIndex).map((part) => part.value).join(''),
        };
      }

      async submit(buyNow) {
        const items = this.getSelection().map(({ id, quantity }) => ({ id, quantity }));
        if (!items.length) return;

        const buttons = this.querySelectorAll('[data-bulk-add], [data-bulk-buy]');
        buttons.forEach((button) => (button.disabled = true));
        this.hideError();

        try {
          const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || 'Unable to add the selected items.');

          const root = window.Shopify?.routes?.root || '/';
          window.location.assign(buyNow ? `${root}checkout` : window.routes?.cart_url || `${root}cart`);
        } catch (error) {
          this.showError(error.message);
          buttons.forEach((button) => (button.disabled = false));
        }
      }

      showError(message) {
        const error = this.querySelector('[data-bulk-error]');
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
      }

      hideError() {
        const error = this.querySelector('[data-bulk-error]');
        if (error) error.hidden = true;
      }
    }
  );
}
